'use client';

import { Modals } from '@/containers/Modals/all-modals';
import { selectPrimaryRootAccount } from '@/lib/redux/app.selectors';
import { useAppDispatch } from '@/lib/redux/store';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AddOptOutModal from './AddOptOutModal/AddOptOutModal';
import AddTrackedLinkModal from './AddTrackedLinkModal/AddTrackedLinkModal';
import AssignAgentsModal from './AssignAgentsModal/AssignAgentsModal';
import AssignContactListModal from './AssignContactListModal/AssignContactListModal';
import AssignJobsModal from './AssignJobsModal/AssignJobsModal';
import AssignSuppressionListsModal from './AssignSuppressionListsModal/AssignSuppressionListsModal';
import CanvasserDetailsModal from './CanvasserDetailsModal/CanvasserDetailsModal';
import ContactListDetailsModal from './ContactListDetailsModal/ContactListDetailsModal';

import { usePathname, useRouter } from 'next/navigation';
import AddAccountTransactionModal from './AddAccountTransactionModal/AddAccountTransactionModal';
import AssignUsersModal from './AssignUsersModal/AssignUsersModal';
import CreateAccountUserModal from './CreateAccountUserModal/CreateAccountUserModal';
import CreateNewAgentModal from './CreateNewAgentModal/CreateNewAgentModal';
import DebitTransactionDetailsModal from './DebitTransactionDetailsModal/DebitTransactionDetailsModal';
import EditUserDetailsModal from './EditUserDetailsModal/EditUserDetailsModal';
import JobTestModal from './JobTestModal/JobTestModal';
import MediaUploadModal from './MediaUploadModal/MediaUploadModal';
import MediaViewerModal from './MediaViewerModal/MediaViewerModal';
import P2PUploadModal from './P2PUploadModal/P2PUploadModal';
import QuestionModal from './QuestionModal/QuestionModal';
import { selectOpenModals } from './redux/modals.selectors';
import { addOpenModal, removeOpenModal } from './redux/modals.slice';
import ReplyTemplateModal from './ReplyTemplateModal/ReplyTemplateModal';
import RequestCanvassersModal from './RequestCanvassersModal/RequestCanvassersModal';
import UserDetailsModal from './UserDetailsModal/UserDetailsModal';
import ViewTrackedLinksModal from './ViewTrackedLinksModal/ViewTrackedLinksModal';
import ExportReportModal from './ExportReportModal/ExportReportModal';

interface AllModalsProps {
  className?: string;
}

const AllModals = ({ className }: AllModalsProps) => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const rootAccount = useSelector(selectPrimaryRootAccount);
  const openModals = useSelector(selectOpenModals);

  const [modal, setModal] = useState(searchParams.get('modal'));
  const [params, setParams] = useState(searchParams.get('modal-params'));

  useEffect(() => {
    const newModal = searchParams.get('modal');
    const newParams = searchParams.get('modal-params');

    if (modal !== newModal) {
      setModal(searchParams.get('modal'));
    }
    if (params !== newParams) {
      setParams(searchParams.get('modal-params'));
    }
  }, [modal, params, searchParams]);

  useEffect(() => {
    switch (modal) {
      case Modals.P2PUploadModal:
        dispatch(
          addOpenModal({
            modalName: Modals.P2PUploadModal,
            modalParams: params ?? undefined,
          })
        );

        break;
      case Modals.AssignContactListModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AssignContactListModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.AssignAgentsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AssignAgentsModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.AssignJobsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AssignJobsModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.CreateNewAgent:
        dispatch(
          addOpenModal({
            modalName: Modals.CreateNewAgent,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.AssignSuppressionListsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AssignSuppressionListsModal,
            modalParams: params ?? undefined,
          })
        );
        break;

      case Modals.ViewTrackedLinksModal:
        dispatch(
          addOpenModal({
            modalName: Modals.ViewTrackedLinksModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.AddTrackedLinkModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AddTrackedLinkModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.MediaViewerModal:
        dispatch(
          addOpenModal({
            modalName: Modals.MediaViewerModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case Modals.MediaUploadModal:
        dispatch(
          addOpenModal({
            modalName: Modals.MediaUploadModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.JobTestModal:
        dispatch(
          addOpenModal({
            modalName: Modals.JobTestModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.ReplyTemplateModal:
        dispatch(
          addOpenModal({
            modalName: Modals.ReplyTemplateModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.QuestionModal:
        dispatch(
          addOpenModal({
            modalName: Modals.QuestionModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.RequestCanvassersModal:
        dispatch(
          addOpenModal({
            modalName: Modals.RequestCanvassersModal,
            modalParams: params ?? undefined,
          })
        );
        break;

      case Modals.AddOptOutModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AddOptOutModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.ContactListDetailsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.ContactListDetailsModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case Modals.UserDetailsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.UserDetailsModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case Modals.EditUserDetailsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.EditUserDetailsModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.CanvasserDetailsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.CanvasserDetailsModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case Modals.AssignUsersModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AssignUsersModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.CreateAccountUserModal:
        dispatch(
          addOpenModal({
            modalName: Modals.CreateAccountUserModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.AddAccountTransactionModal:
        dispatch(
          addOpenModal({
            modalName: Modals.AddAccountTransactionModal,
            modalParams: params ?? undefined,
          })
        );
        break;
      case Modals.DebitTransactionDetailsModal:
        dispatch(
          addOpenModal({
            modalName: Modals.DebitTransactionDetailsModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case Modals.ExportReportModal:
        dispatch(
          addOpenModal({
            modalName: Modals.ExportReportModal,
            modalParams: params ?? undefined,
            shouldCloseOnEscape: true,
          })
        );
        break;
      case '':
      default:
        break;
    }
  }, [dispatch, modal, params]);

  const sortedModals = useMemo(
    () => [...openModals].sort((a, b) => (a.modalOrder ?? 0) - (b.modalOrder ?? 0)),
    [openModals]
  );

  // Global escape key handler for modals that should close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Find the topmost modal that should close on escape
        const sortedEscapeModals = sortedModals
          .filter((modal) => modal.shouldCloseOnEscape)
          .sort((a, b) => (b.modalOrder ?? 0) - (a.modalOrder ?? 0));

        const modalToClose = sortedEscapeModals[0];
        if (modalToClose) {
          // Close the modal by removing from redux
          dispatch(removeOpenModal(modalToClose.modalName));

          // Clear URL parameters
          const params = new URLSearchParams(searchParams);
          params.delete('modal');
          params.delete('modal-params');

          const paramsString = params.toString() ? `?${params.toString()}` : '';
          const newUrl = `${pathname}${paramsString}`;
          router.replace(newUrl);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, sortedModals, searchParams, pathname, router]);

  if (!rootAccount) {
    return;
  }

  return (
    <div className={classNames('', className)}>
      {sortedModals.map((modal, idx) => {
        if (modal.modalName === Modals.P2PUploadModal) {
          return (
            <P2PUploadModal
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              variant={modal.modalVariant ?? 'default'}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AssignContactListModal) {
          return (
            <AssignContactListModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AssignAgentsModal) {
          return (
            <AssignAgentsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AssignJobsModal) {
          return (
            <AssignJobsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.CreateNewAgent) {
          return (
            <CreateNewAgentModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }

        if (modal.modalName === Modals.ViewTrackedLinksModal) {
          return (
            <ViewTrackedLinksModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AddTrackedLinkModal) {
          return (
            <AddTrackedLinkModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AssignSuppressionListsModal) {
          return (
            <AssignSuppressionListsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.MediaViewerModal) {
          return (
            <MediaViewerModal
              variant={modal.modalVariant ?? 'default'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.MediaUploadModal) {
          return (
            <MediaUploadModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.JobTestModal) {
          return (
            <JobTestModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.ReplyTemplateModal) {
          return (
            <ReplyTemplateModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.QuestionModal) {
          return (
            <QuestionModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.RequestCanvassersModal) {
          return (
            <RequestCanvassersModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }

        if (modal.modalName === Modals.AddOptOutModal) {
          return (
            <AddOptOutModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.ContactListDetailsModal) {
          return (
            <ContactListDetailsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.UserDetailsModal) {
          return (
            <UserDetailsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.EditUserDetailsModal) {
          return (
            <EditUserDetailsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.CanvasserDetailsModal) {
          return (
            <CanvasserDetailsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AssignUsersModal) {
          return (
            <AssignUsersModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.CreateAccountUserModal) {
          return (
            <CreateAccountUserModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.AddAccountTransactionModal) {
          return (
            <AddAccountTransactionModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.DebitTransactionDetailsModal) {
          return (
            <DebitTransactionDetailsModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        if (modal.modalName === Modals.ExportReportModal) {
          return (
            <ExportReportModal
              variant={modal.modalVariant ?? 'narrow'}
              key={`${idx}-${modal.modalName}-${modal.modalOrder ?? 0}`}
              params={modal.modalParams ? JSON.parse(modal.modalParams) : undefined}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default AllModals;
